import React, { useEffect, useMemo } from "react";
import { Stack, Typography } from "@mui/material";

import {
  HubCancelSubmitGroup,
  HubDialogContent,
  HubHookFormWrapper,
} from "../../components";
import {
  useGetMaterialDetails,
  usePostCreateMaterial,
  usePutMaterial,
} from "../../hooks";
import { MATERIAL_TYPES_DICT } from "../../constants/materials";
import { useDialogContext } from "../HubDialogWrapper";

import CreateMaterialForm from "./CreateMaterialForm";

const toNumberOrNull = (val) => {
  if (val == null || val === "") return null;
  const num = Number(val);
  return isNaN(num) ? null : num;
};

const CreateMaterialContent = () => {
  const { onClose, open } = useDialogContext();

  // open === true              → create (no defaults)
  // open === { onSuccess, defaultValue } → create (used by autocomplete "+ new")
  // open === number            → edit (material id)
  const isEdit = typeof open === "number";
  const editId = isEdit ? open : null;
  const createOptions = !isEdit && typeof open === "object" ? open : {};
  const { onSuccess: createOnSuccess = () => {}, defaultValue = "" } =
    createOptions;

  const { data: existing, isLoading: isLoadingDetails } =
    useGetMaterialDetails(editId);

  const {
    data: created,
    mutate: postCreateMaterial,
    isLoading: isCreating,
    isSuccess: isCreated,
  } = usePostCreateMaterial();

  const {
    mutate: putMaterial,
    isLoading: isUpdating,
    isSuccess: isUpdated,
  } = usePutMaterial();

  useEffect(() => {
    if (isCreated) {
      createOnSuccess(created);
      onClose();
    }
  }, [isCreated]);

  useEffect(() => {
    if (isUpdated) onClose();
  }, [isUpdated]);

  const defaultValues = useMemo(() => {
    if (!isEdit) {
      return { type: MATERIAL_TYPES_DICT.MATERIAL };
    }
    if (!existing) return undefined;
    return {
      type: MATERIAL_TYPES_DICT.MATERIAL,
      name: existing.name,
      weight: existing.weight,
      category: existing.category,
      remelt: !existing.noRemelt,
      prices: existing.pricesByLocation || {},
    };
  }, [isEdit, existing]);

  const onSubmit = (formData) => {
    const type = Number(formData.type);

    // Edit only handles materials (not trees) for now
    if (isEdit) {
      const prices = formData.prices || {};
      putMaterial({
        id: editId,
        name: formData.name,
        weight: parseFloat(formData.weight),
        category: formData.category,
        noRemelt: !formData.remelt,
        prices: Object.fromEntries(
          Object.entries(prices).map(([k, v]) => [k, toNumberOrNull(v)]),
        ),
      });
      return;
    }

    const newMaterial = {
      type,
      name: formData.name,
      weight: parseFloat(formData.weight),
    };

    if (type === MATERIAL_TYPES_DICT.MATERIAL) {
      newMaterial.category = formData?.category;
      newMaterial.noRemelt = !formData?.remelt;
      const prices = formData.prices || {};
      newMaterial.prices = Object.fromEntries(
        Object.entries(prices).map(([k, v]) => [k, toNumberOrNull(v)]),
      );
    } else if (type === MATERIAL_TYPES_DICT.TREE) {
      newMaterial.locationId = Number(formData?.locationId);
    }

    postCreateMaterial(newMaterial);
  };

  const submitting = isCreating || isUpdating;

  return (
    <HubDialogContent largePadding>
      <HubHookFormWrapper
        defaultValues={defaultValues}
        onSubmit={onSubmit}
      >
        <Stack gap="35px" alignItems="center">
          <Stack alignItems="center">
            <Typography variant="h6">
              {isEdit ? "Edit Material" : "Create a New Material"}
            </Typography>
            <Typography variant="caption">
              {isEdit
                ? "Update fields and per-location pricing"
                : "Use the fields below to create a new material"}
            </Typography>
          </Stack>
          <CreateMaterialForm
            defaultValue={defaultValue}
            isEdit={isEdit}
            isLoadingDetails={isEdit && isLoadingDetails}
          />
          <HubCancelSubmitGroup
            disabled={submitting || (isEdit && isLoadingDetails)}
            isLoading={submitting}
            label={isEdit ? "Save Changes" : "Create Material"}
          />
        </Stack>
      </HubHookFormWrapper>
    </HubDialogContent>
  );
};

export default CreateMaterialContent;
